/**
 * TagEditor — the one write surface for a trace's tags (TRA-939).
 *
 * Renders the current tags as chips plus a popover to add/remove them.
 * Every change posts the FULL tag set through the dedicated tags upsert,
 * which never touches the review verdict — and the review composer's label
 * upsert never touches tags, so the two can't clobber each other.
 *
 * Used from the trajectories table rows and the review composer.
 */

import { Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useTagVocabulary, useUpsertTags } from '@/hooks/useLabels'

interface TagEditorProps {
  traceId: string
  traceKey: string
  /**
   * Set when the trace belongs to a conversation and this editor represents
   * the RUN's tags (the trajectories page): saves are conversation-scoped,
   * so removing a tag removes it from the whole run, not just this trace.
   */
  conversationId?: string
  /** Initial tag set — the editor keeps its own working copy after mount. */
  tags: string[]
  /**
   * Cap on inline chips for tight surfaces (the review header): overflow
   * collapses into the popover trigger as `+ N`. The popover always lists
   * the full set. Omitted = every chip renders (the trajectories table).
   */
  maxVisible?: number
}

export function TagEditor({
  traceId,
  traceKey,
  conversationId,
  tags: initialTags,
  maxVisible,
}: TagEditorProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  // Working copy of the tag set. Each save composes on THIS, not on the
  // parent's (possibly stale) prop — two quick additions before the list
  // query refetches must not overwrite each other. The prop re-seeds it
  // when the server round-trip lands a different set.
  const [tags, setTags] = useState(initialTags)
  const initialKey = initialTags.join(' ')
  useEffect(() => {
    setTags(initialTags)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on content: the array identity changes every refetch
  }, [initialKey])
  const vocabulary = useTagVocabulary()
  const upsert = useUpsertTags()

  async function save(next: string[]): Promise<void> {
    setTags(next)
    try {
      await upsert.mutateAsync({
        data: {
          trace_id: traceId,
          trace_key: traceKey,
          tags: next,
          ...(conversationId !== undefined
            ? { conversation_id: conversationId }
            : {}),
        },
      })
    } catch (error) {
      setTags(tags)
      toast.error(`Failed to save tags: ${String(error)}`)
    }
  }

  function addTag(tag: string): void {
    const cleaned = tag.trim().replace(/\s+/g, ' ')
    if (!cleaned) return
    const exists = tags.some(
      (t) => t.toLowerCase() === cleaned.toLowerCase(),
    )
    setDraft('')
    if (exists) return
    void save([...tags, cleaned])
  }

  function removeTag(tag: string): void {
    void save(tags.filter((t) => t !== tag))
  }

  // Vocabulary entries not already on the trace, narrowed by the draft text.
  const suggestions = (vocabulary.data?.tags ?? []).filter(
    (tag) =>
      !tags.some((t) => t.toLowerCase() === tag.toLowerCase()) &&
      tag.toLowerCase().includes(draft.trim().toLowerCase()),
  )

  const visibleTags = maxVisible === undefined ? tags : tags.slice(0, maxVisible)
  const hiddenCount = tags.length - visibleTags.length

  return (
    <div className="flex flex-wrap items-center gap-1">
      {/* Chips open the same editor the + does — removal lives in the
          popover, so an inert chip would hide that it exists at all. */}
      {visibleTags.map((tag) => (
        <button
          key={tag}
          type="button"
          title={`${tag} (edit tags)`}
          onClick={(event) => {
            event.stopPropagation()
            setOpen(true)
          }}
          className="inline-flex h-5 max-w-[200px] items-center whitespace-nowrap gap-1 border border-border bg-muted px-1.5 font-mono text-[11px] text-foreground hover:border-muted-foreground"
        >
          <span className="truncate">{tag}</span>
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Edit tags for trace ${traceId}`}
            title={hiddenCount > 0 ? tags.join(', ') : undefined}
            onClick={(event) => {
              event.stopPropagation()
            }}
            className="inline-flex h-5 items-center whitespace-nowrap gap-0.5 border border-dashed border-border px-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:border-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3 w-3" />
            {tags.length === 0 ? 'Tag' : hiddenCount > 0 ? hiddenCount.toString() : ''}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-64 p-2"
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <div className="space-y-2">
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    aria-label={`Remove tag ${tag}`}
                    disabled={upsert.isPending}
                    onClick={() => {
                      removeTag(tag)
                    }}
                    title={tag}
                    className="inline-flex h-5 max-w-[200px] items-center whitespace-nowrap gap-1 border border-border bg-muted px-1.5 font-mono text-[11px] text-foreground hover:border-destructive/50 hover:text-destructive disabled:opacity-50"
                  >
                    <span className="truncate">{tag}</span>
                    <X className="h-3 w-3 shrink-0" />
                  </button>
                ))}
              </div>
            )}
            <Input
              autoFocus
              value={draft}
              placeholder="Add a tag…"
              aria-label="New tag"
              // A bare text input reads as a username field to password
              // managers, which offer to fill saved logins over the tag
              // popover. Each manager honors its own opt-out attribute.
              autoComplete="off"
              data-bwignore=""
              data-1p-ignore=""
              data-lpignore="true"
              disabled={upsert.isPending}
              onChange={(event) => {
                setDraft(event.target.value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addTag(draft)
                }
              }}
              className="h-7 font-mono text-xs"
            />
            {suggestions.length > 0 && (
              <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                {suggestions.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    disabled={upsert.isPending}
                    onClick={() => {
                      addTag(tag)
                    }}
                    title={tag}
                    className="inline-flex h-5 max-w-[200px] items-center whitespace-nowrap border border-border px-1.5 font-mono text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    <span className="truncate">{tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
